<script setup lang="ts">
import { computed, ref } from 'vue';

const roleOptions = [
  'Massage Therapist',
  'Nail Technician',
  'Beauty Therapist',
  'Front Desk / Reception',
  'Other'
];

const employmentTypeOptions = [
  'Full-time',
  'Part-time',
  'Contract / Freelance',
  'Not sure yet'
];

const rightToWorkOptions = ['Yes', 'No'];

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const phone = ref('');
const role = ref('');
const employmentType = ref('');
const experience = ref('');
const skills = ref('');
const availability = ref('');
const rightToWork = ref('');
const visaType = ref('');
const cvFiles = ref<File[] | File | null>(null);
const consent = ref(false);

const requiresVisaType = computed(() => rightToWork.value === 'No');

const requiredRule = (value: string | boolean | File[] | File | null): true | string => {
  if (Array.isArray(value)) {
    return value.length > 0 || 'This field is required';
  }
  return (!!value && String(value).trim().length > 0) || 'This field is required';
};

function handleSubmit() {
}
</script>

<template>
  <v-parallax class="hero" src="/img/team/jasmine.webp" alt="Jasmine Therapies team">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2 animate-text">A calm space for healing and growth</div>
        <div class="text-center text-h2 pa-2 animate-title">Join our Team</div>
      </div>
      <div class="text-center animate-button">
        <v-btn color="primary" href="#application-form" size="large" class="book-now-button" elevation="4">Apply now</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-join-us">
    <div class="full-width-header">Work with us</div>
    <div class="content pb-16">
      <p class="content-intro">
        We are always happy to connect with kind, skilled professionals who care deeply about client wellbeing.
        If our approach resonates with you, we'd love to learn a little about you.
      </p>

      <div class="feature-grid">
        <div class="feature-item">
          <div class="feature-title">Supportive Environment</div>
          <div class="feature-text">A warm, respectful workplace focused on quality care and team wellbeing.</div>
        </div>
        <div class="feature-item">
          <div class="feature-title">Professional Standards</div>
          <div class="feature-text">Client-first service with attention to detail, consistency, and trust.</div>
        </div>
        <div class="feature-item">
          <div class="feature-title">Long-Term Growth</div>
          <div class="feature-text">Opportunities to grow your role as the business continues to expand.</div>
        </div>
      </div>
    </div>
  </section>

  <section id="application-form">
    <div class="full-width-header">Application Form</div>
    <div class="content pb-16">
      <p class="content-intro form-intro">
        Please share your details below. For now, clicking submit does not send data yet — this page is currently collecting structure only.
      </p>

      <v-card class="application-card" rounded="xl" elevation="2">
        <v-card-text class="pa-6 pa-md-8">
          <v-form @submit.prevent="handleSubmit">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="firstName" label="First name *" variant="outlined" :rules="[requiredRule]" required />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="lastName" label="Last name *" variant="outlined" :rules="[requiredRule]" required />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="email" label="Email address *" type="email" variant="outlined" :rules="[requiredRule]" required />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="phone" label="Phone number" type="tel" variant="outlined" />
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="role" :items="roleOptions" label="Position applying for *" variant="outlined" :rules="[requiredRule]" required />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="employmentType" :items="employmentTypeOptions" label="Employment type" variant="outlined" />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="rightToWork"
                  :items="rightToWorkOptions"
                  label="Do you currently have the right to work in Ireland? *"
                  variant="outlined"
                  :rules="[requiredRule]"
                  required />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="visaType"
                  label="If no, what visa do you hold?"
                  variant="outlined"
                  :disabled="!requiresVisaType"
                  :placeholder="requiresVisaType ? 'e.g., Stamp 1, Stamp 1G' : ''" />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="experience"
                  label="Relevant work experience *"
                  variant="outlined"
                  rows="4"
                  auto-grow
                  :rules="[requiredRule]"
                  required />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="skills"
                  label="Key skills / services you offer *"
                  variant="outlined"
                  rows="3"
                  auto-grow
                  :rules="[requiredRule]"
                  required />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="availability"
                  label="Availability (days / hours) *"
                  variant="outlined"
                  rows="3"
                  auto-grow
                  :rules="[requiredRule]"
                  required />
              </v-col>

              <v-col cols="12" md="6">
                <v-file-input
                  v-model="cvFiles"
                  label="Upload your CV *"
                  variant="outlined"
                  accept=".pdf,.doc,.docx"
                  show-size
                  :rules="[requiredRule]"
                  required />
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="consent"
                  :rules="[requiredRule]"
                  label="I agree to be contacted regarding this application and understand my details are shared for recruitment purposes only. *"
                  required
                  hide-details="auto" />
              </v-col>

              <v-col cols="12" class="d-flex justify-center pt-2">
                <v-btn type="submit" color="primary" size="large" class="book-now-button">
                  Submit application
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </div>
  </section>
</template>

<style scoped>
.application-card {
  max-width: 980px;
  margin: 0 auto;
  background-color: rgba(var(--v-theme-primary), 0.08);
  border: 1px solid rgba(var(--v-theme-primary), 0.32);
}

.form-intro {
  margin-bottom: 32px;
}

.animate-text, .animate-title, .animate-button {
  animation-duration: 1.5s;
  animation-fill-mode: both;
}

.animate-text {
  animation-name: fadeInDown;
  animation-delay: 0.3s;
}

.animate-title {
  animation-name: fadeInUp;
  animation-delay: 0.6s;
}

.animate-button {
  animation-name: fadeIn;
  animation-delay: 1s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate3d(0, -30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}
</style>